import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const TreatmentModal = ({ show, handleClose, treatment }) => {
    const { t } = useTranslation();
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Ensure images is an array; fallback to placeholder
    const images = Array.isArray(treatment.images) && treatment.images.length > 0
        ? treatment.images
        : ['/path/to/placeholder.jpg'];

    // Handle image click to enlarge
    const handleImageClick = (img) => {
        setEnlargedImage(img);
    };

    // Handle close enlarged image
    const handleCloseEnlarged = () => {
        setEnlargedImage(null);
    };

    // Handle carousel navigation
    const handlePrev = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    // Reset image index when treatment changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [treatment]);

    return (
        <>
            {/* Full-screen image view */}
            {enlargedImage && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
                    <div className="relative max-w-4xl w-full p-4">
                        <img
                            src={enlargedImage}
                            className="w-full max-h-[80vh] object-contain"
                            alt={t('spaSection.enlargedImageAlt')}
                            onError={(e) => (e.target.src = '/path/to/placeholder.jpg')}
                        />
                        <button
                            className="absolute top-4 right-4 text-white text-3xl bg-gray-800/50 p-2 rounded-full hover:bg-gray-800/75 transition"
                            onClick={handleCloseEnlarged}
                            aria-label={t('spaSection.closeEnlargedImage')}
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}

            {/* Main modal */}
            <div
                className={`fixed inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                aria-hidden={!show}
            >
                <div className="bg-white rounded-lg w-full max-w-xs sm:max-w-md md:max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="relative p-4 sm:p-6">
                        <h5 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center">
                            {treatment.name || t('spaSection.defaultTreatmentName')}
                        </h5>
                        <button
                            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl"
                            onClick={handleClose}
                            aria-label={t('spaSection.closeModal')}
                        >
                            &times;
                        </button>
                    </div>
                    <div className="p-4 sm:p-6">
                        {/* Carousel for images */}
                        <div className="relative mb-6">
                            <div className="overflow-hidden rounded-lg">
                                {images.map((img, index) => (
                                    <div
                                        key={index}
                                        className={`${index === currentImageIndex ? 'block' : 'hidden'} duration-700 ease-in-out`}
                                    >
                                        <img
                                            src={img}
                                            className="w-auto h-48 sm:h-64 md:h-96 object-cover cursor-pointer"
                                            alt={t(`spaSection.treatments.${treatment.name.toLowerCase().replace(' ', '')}.name`)}
                                            onClick={() => handleImageClick(img)}
                                            onError={(e) => (e.target.src = '/path/to/placeholder.jpg')}
                                        />
                                    </div>
                                ))}
                            </div>
                            {images.length > 1 && (
                                <>
                                    <button
                                        className="absolute top-1/2 left-2 -translate-y-1/2 bg-gray-800/50 text-white p-2 sm:p-3 rounded-full hover:bg-gray-800/75 transition"
                                        onClick={handlePrev}
                                        aria-label={t('spaSection.previousImage')}
                                    >
                                        <svg
                                            className="w-5 h-5 sm:w-6 sm:h-6"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M15 19l-7-7 7-7"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        className="absolute top-1/2 right-2 -translate-y-1/2 bg-gray-800/50 text-white p-2 sm:p-3 rounded-full hover:bg-gray-800/75 transition"
                                        onClick={handleNext}
                                        aria-label={t('spaSection.nextImage')}
                                    >
                                        <svg
                                            className="w-5 h-5 sm:w-6 sm:h-6"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M9 5l-7 7 7-7"
                                            />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>
                        <h6 className="text-lg sm:text-xl font-medium mb-2">{t('spaSection.descriptionTitle')}</h6>
                        <p className="text-gray-700 mb-4 text-sm sm:text-base">
                            {treatment.description || t('spaSection.noDescription')}
                        </p>
                        <h6 className="text-lg sm:text-xl font-medium mb-2">{t('spaSection.benefitsTitle')}</h6>
                        <ul className="list-disc pl-5 text-sm sm:text-base">
                            {Array.isArray(treatment.benefits) && treatment.benefits.length > 0 ? (
                                treatment.benefits.map((benefit, index) => (
                                    <li key={index} className="text-gray-700">
                                        {benefit}
                                    </li>
                                ))
                            ) : (
                                <li className="text-gray-700">{t('spaSection.noBenefits')}</li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TreatmentModal;